import { Box, Stack } from '@mui/material';
import { ArrowLeftToLine, ArrowRightToLine, LockKeyhole, Scaling, Trash2 } from 'lucide-react';
import React, { useCallback, useRef, useState } from 'react';
import { useAnalysisStore } from '../../stores/analysisStore';
import { ButtonPush } from '../ui/ButtonPush';
import O2kChartView from './O2kChartView';

/**
 * O2kChartPanel — container for one chamber's O2K editor.
 * Reads from analysisStore and passes data as props to O2kChartView.
 */
const O2kChartPanel = ({ tabId, chamberIndex }) => {
  const graphControlsRef = useRef({});
  const [isLocked, setIsLocked] = useState(false);

  const chamber = useAnalysisStore((state) => state.openFiles[tabId]?.chambers?.[chamberIndex]);

  const {
    setTransitionsLocked,
    deleteTransition,
    addTransition,
    updateTransition,
    removeTransition,
    setSelectedTransition,
  } = useAnalysisStore();

  const toggleLock = () => {
    const next = !isLocked;
    setIsLocked(next);
    graphControlsRef.current.setTransitionsLocked?.(next);
    setTransitionsLocked(tabId, chamberIndex, next);
  };

  const handleDeleteSelected = async () => {
    if (!chamber?.selectedTransitionId) return;
    try {
      await deleteTransition(tabId, chamberIndex, chamber.selectedTransitionId);
    } catch (err) {
      console.error('Failed to delete transition:', err);
    }
  };

  const handleAddTransition = useCallback(
    (t) => addTransition(tabId, chamberIndex, t),
    [tabId, chamberIndex, addTransition],
  );

  const handleUpdateTransition = useCallback(
    (id, updater) => updateTransition(tabId, chamberIndex, id, updater),
    [tabId, chamberIndex, updateTransition],
  );

  const handleRemoveTransition = useCallback(
    (id) => removeTransition(tabId, chamberIndex, id),
    [tabId, chamberIndex, removeTransition],
  );

  const handleSelectTransition = useCallback(
    (id) => setSelectedTransition(tabId, chamberIndex, id),
    [tabId, chamberIndex, setSelectedTransition],
  );

  const chamberLabel = String(chamberIndex);

  return (
    <Box
      sx={{
        height: '100%',
        display: 'flex',
        gap: '2px',
        userSelect: 'none',
        WebkitUserSelect: 'none',
      }}
    >
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <O2kChartView
          controlsRef={graphControlsRef}
          chamber={chamberLabel}
          height="100%"
          chamberData={chamber?.data || null}
          transitions={chamber?.transitions || []}
          transitionsLoading={chamber?.transitionsLoading || false}
          selectedTransitionId={chamber?.selectedTransitionId || null}
          onAddTransition={handleAddTransition}
          onUpdateTransition={handleUpdateTransition}
          onRemoveTransition={handleRemoveTransition}
          onSelectTransition={handleSelectTransition}
        />
      </Box>
      <Stack
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: '12px',
          marginRight: '8px',
          marginLeft: '16px',
          marginTop: '36px',
          flex: '0 0 auto',
        }}
      >
        <ButtonPush
          borderColor="transparent"
          tooltipText="Reset Zoom"
          onClick={() => graphControlsRef.current.resetZoom?.()}
        >
          <Scaling strokeWidth={1.5} size={20} />
        </ButtonPush>
        <ButtonPush
          borderColor="transparent"
          tooltipText="Jump to next transition"
          onClick={() => graphControlsRef.current.jumpToNextTransition?.()}
        >
          <ArrowRightToLine strokeWidth={1.5} size={18} />
        </ButtonPush>
        <ButtonPush
          borderColor="transparent"
          tooltipText="Jump to previous transition"
          onClick={() => graphControlsRef.current.jumpToPreviousTransition?.()}
        >
          <ArrowLeftToLine strokeWidth={1.5} size={18} />
        </ButtonPush>
        <ButtonPush
          borderColor="transparent"
          tooltipText="Delete selected transition"
          disabled={isLocked}
          onClick={handleDeleteSelected}
        >
          <Trash2 strokeWidth={1.5} size={18} />
        </ButtonPush>
        <ButtonPush
          borderColor="transparent"
          tooltipText={isLocked ? 'Unlock transitions' : 'Lock transitions'}
          variant={isLocked ? 'active' : 'default'}
          onClick={toggleLock}
        >
          <LockKeyhole strokeWidth={1.5} size={18} />
        </ButtonPush>
      </Stack>
    </Box>
  );
};

export default O2kChartPanel;
